import React from "react";
import { Info } from "lucide-react";
import { Batch } from "@/api/getCampaignBatches/types";
import CampaignBatchActionMenu from "../CampaignBatchActionMenu/CampaignBatchActionMenu";
import { Column } from "@/components/Datatable/types";
import permissionsService from "@/services/permissionsService";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CreateCampaignBatchesColumnsProps {
  handleViewProspects: (id: number) => void;
  handleDownloadCsv: (id: number) => void;
  handleOpenCancelBatchModal: (id: number) => void;
}

const BatchStatusCell: React.FC<{
  batch: Batch;
  onClick: () => void;
}> = ({ batch, onClick }) => {
  const { hasPermission } = permissionsService();
  const canViewCancellationDetails = hasPermission("CAN-CANCEL-BATCH");

  const name = batch.batchStatus?.name;
  if (!name) {
    return (
      <div className="cursor-pointer hover:text-blue-600" onClick={onClick}>
        N/A
      </div>
    );
  }

  const capitalized = name.charAt(0).toUpperCase() + name.slice(1);
  const isCanceled = name.toLowerCase() === "canceled";
  const canceledEvent = batch.canceledEvent;

  return (
    <div className="flex items-center gap-1">
      <span className="cursor-pointer hover:text-blue-600" onClick={onClick}>
        {capitalized}
      </span>
      {isCanceled && canViewCancellationDetails && canceledEvent && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-lg">
              Batch was canceled by {canceledEvent.employeeName} on{" "}
              {new Date(canceledEvent.canceledAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}{" "}
              {new Date(canceledEvent.canceledAt).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
              })}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

export function createCampaignBatchesColumns({
  handleViewProspects,
  handleDownloadCsv,
  handleOpenCancelBatchModal,
}: CreateCampaignBatchesColumnsProps): Column<Batch>[] {
  return [
    {
      header: "Batch ID",
      enableSorting: true,
      accessorKey: "id",
      cell: ({ row }) => (
        <div
          className="cursor-pointer hover:text-blue-600"
          onClick={() => handleViewProspects(row.original.id)}
        >
          {row.original.id}
        </div>
      ),
    },
    {
      header: "Week",
      accessorKey: "week_number",
      enableSorting: true,
      cell: ({ row }) => (
        <div
          className="cursor-pointer hover:text-blue-600"
          onClick={() => handleViewProspects(row.original.id)}
        >
          {row.original.campaignIterationWeek?.week_number ?? "N/A"}
        </div>
      ),
    },
    {
      header: "Batch Status",
      accessorKey: "batchStatus",
      enableSorting: true,
      cell: ({ row }) => (
        <BatchStatusCell
          batch={row.original}
          onClick={() => handleViewProspects(row.original.id)}
        />
      ),
    },
    {
      header: "Prospects Count",
      accessorKey: "prospectsCount",
      enableSorting: true,
      cell: ({ row }) => (
        <div
          className="cursor-pointer hover:text-blue-600"
          onClick={() => handleViewProspects(row.original.id)}
        >
          {row.original.prospectsCount ?? "N/A"}
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <CampaignBatchActionMenu
          batch={row.original}
          onDownloadCsv={handleDownloadCsv}
          onOpenCancelBatchModal={handleOpenCancelBatchModal}
          onViewProspects={handleViewProspects}
        />
      ),
    },
  ];
}
